import React from 'react';

import { useTranslation } from 'react-i18next';
import { Block, Typography, Heading } from '@klp/design';

import { ConsultantSelector } from '../../ConsultantSelector';
import type { IContractToRegistrationProcessActivity } from '../../../classes/ContractToRegistrationProcessResponse';
import type { RegistrationStatuses } from '../../../services/rtkApi';
import { Activities, ConsultantRoles } from '../../../services/rtkApi';
import {
    handleRequiredFormFields,
    useMapUserRolesToCandidateGroup,
} from '../../../utils';
import { FormContainerKontraktsforvaltning } from '@klp/formgenerator/index';
import { useSelector } from 'react-redux';
import { contractregistrationSelectors } from '@klp/contractregistration/contractToRegistrationSlice';

type AssignFinancialStepProps = {
    handleCompleteStep: () => void;
    activity: IContractToRegistrationProcessActivity;
    prosessInstansId: string;
    status: RegistrationStatuses.TILDELT_OKONOMI;
};

export default function AssignFinancialStep({
    handleCompleteStep,
    status,
    activity,
    prosessInstansId,
}: AssignFinancialStepProps) {
    const { t } = useTranslation();
    const { formFieldProcessing, formFieldError } = useSelector(
        contractregistrationSelectors,
    );

    const { isUserCandidateForActivity } =
        useMapUserRolesToCandidateGroup(activity);

    const formFieldIsRequired = handleRequiredFormFields(activity?.formFields);

    const baseIsDisabled =
        activity.id === null ||
        activity.utfortDato !== null ||
        !isUserCandidateForActivity;

    const submitIsDisabled =
        baseIsDisabled ||
        formFieldProcessing ||
        formFieldError ||
        formFieldIsRequired;

    return (
        <>
            <Block paddingLeft="compact">
                {activity.utfortDato === null && (
                    <Heading
                        klpHeadingLevel="THREE"
                        klpColor="dark"
                        translation={`checkOffContract.step.${activity?.taskDefinitionId}.secondaryTitle`}
                    />
                )}
            </Block>
            <Block paddingLeft="compact" paddingTop="tiny">
                {activity.utfortDato === null && (
                    <>
                        <Typography>
                            {t(
                                `checkOffContract.step.${activity?.taskDefinitionId}.descriptionPartOne`,
                            )}
                        </Typography>
                        <Typography>
                            {t(
                                `checkOffContract.step.${activity?.taskDefinitionId}.descriptionPartTwo`,
                            )}
                        </Typography>
                    </>
                )}

                {activity.formFields && (
                    <Block>
                        <FormContainerKontraktsforvaltning
                            activity={activity}
                            prosessInstansId={prosessInstansId}
                            isDisabled={baseIsDisabled}
                        />
                    </Block>
                )}

                <ConsultantSelector
                    status={
                        status && {
                            TildeltOkonomi: status,
                        }
                    }
                    activity={
                        activity && {
                            [Activities.TILDEL_OKONOMI]: activity,
                        }
                    }
                    filter="økonomi"
                    completeTask={true}
                    completeStepCallback={handleCompleteStep}
                    isDisabled={baseIsDisabled}
                    submitIsDisabled={submitIsDisabled}
                    sortBy={ConsultantRoles.OKONOMI}
                />
            </Block>
        </>
    );
}
